/**
 * De naam die een palette-drag meegeeft aan het nieuwe concept.
 *
 * Het palet zet twee payloads: het label in de UI-taal (oud, één string) en
 * de naam in beide talen als JSON. De drop-plekken lezen de tweetalige naam,
 * zodat een object dat in het Engels is neergezet in het Nederlands de
 * Nederlandse typenaam krijgt (en andersom). Het enkele label blijft als
 * terugval voor drags die alleen dat meegeven.
 */

export const TYPE_LABEL_MIME = "application/ae-type-label";
export const TYPE_NAME_MIME = "application/ae-type-name";

// Een type-alias (geen interface), zodat 't ook als BilingualText
// (Record<string, string>) door kan.
export type DroppedTypeName = { nl: string; en: string };

const nonEmpty = (v: unknown): v is string => typeof v === "string" && v.trim().length > 0;

/** Vult een ontbrekende taal aan met de andere; null als er geen naam is. */
export function bilingualTypeName(text: Record<string, string>): DroppedTypeName | null {
  const nl = nonEmpty(text.nl) ? text.nl : undefined;
  const en = nonEmpty(text.en) ? text.en : undefined;
  if (!nl && !en) return null;
  return { nl: nl ?? en ?? "", en: en ?? nl ?? "" };
}

export function setDraggedTypeName(
  dataTransfer: Pick<DataTransfer, "setData">,
  name: Record<string, string>,
  label: string,
): void {
  dataTransfer.setData(TYPE_LABEL_MIME, label);
  const bilingual = bilingualTypeName(name);
  if (bilingual) dataTransfer.setData(TYPE_NAME_MIME, JSON.stringify(bilingual));
}

export function readDroppedTypeName(
  dataTransfer: Pick<DataTransfer, "getData">,
): DroppedTypeName | null {
  const raw = dataTransfer.getData(TYPE_NAME_MIME);
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const name = bilingualTypeName(parsed as Record<string, string>);
        if (name) return name;
      }
    } catch {
      // kapotte payload: val terug op het enkele label
    }
  }
  const label = dataTransfer.getData(TYPE_LABEL_MIME);
  return nonEmpty(label) ? { nl: label, en: label } : null;
}
